import React from 'react';
import { 
  ArrowRight, 
  BookOpen, 
  MessageCircle, 
  GraduationCap, 
  Calculator, 
  Globe, 
  Heart,
  CheckCircle,
  Users,
  Award,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ContactForm from '../components/ContactForm';
import { courses } from '../mock';

const iconMap = {
  MessageCircle,
  GraduationCap,
  BookOpen,
  Calculator,
  Globe,
  Heart
};

const Home = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const navigateToJobs = () => {
    window.location.href = '/jobs';
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/4915216817165', '_blank');
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section with Wave Background */}
      <section id="hero" className="pt-28 md:pt-36 pb-24 md:pb-32 bg-gradient-to-br from-teal-50 via-white to-teal-50/30 relative">
        {/* Decorative Background Shapes */}
        <div className="absolute top-32 left-0 w-72 h-72 bg-teal-100/40 rounded-full blur-3xl -translate-x-1/2"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl translate-x-1/3"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-block mb-6 animate-fade-in">
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-teal-100 text-teal-800 text-sm font-medium">
                  <Award className="w-4 h-4 mr-2" />
                  Jahrelange Erfahrung
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Erfolgreicher lernen mit dem <span className="bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent whitespace-nowrap">Da Vinci Institut</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                Von der 1. Klasse bis zum Abitur – unsere qualifizierten examinierten Lehrkräfte 
                unterstützen Ihr Kind individuell und mit bewährten Methoden zum schulischen Erfolg.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() => scrollToSection('contact')}
                  size="lg"
                  className="bg-teal-600 hover:bg-teal-700 text-white transition-all duration-300 hover:shadow-lg hover:scale-105 text-base px-8 rounded-full"
                >
                  Kostenlose Beratung
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={openWhatsApp}
                  size="lg"
                  variant="outline"
                  className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 transition-all duration-300 text-base px-8 rounded-full"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  WhatsApp Kontakt
                </Button>
              </div>
            </div>
            
            {/* Right Image with Organic Shape */}
            <div className="relative">
              <div className="relative">
                {/* Background Blob */}
                <div className="absolute inset-0 bg-teal-200/40 rounded-[3rem] -rotate-6 scale-105"></div>
                {/* Image */}
                <img 
                  src="https://images.pexels.com/photos/5063392/pexels-photo-5063392.jpeg"
                  alt="Kind beim Lernen"
                  className="relative w-full h-[400px] md:h-[500px] object-cover rounded-[3rem] shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 50C240 80 480 100 720 80C960 60 1200 30 1440 50V100H0V50Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Dual CTA Section */}
      <section className="py-16 bg-white relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* For Families */}
            <Card className="border-2 border-teal-200 hover:border-teal-400 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 rounded-3xl overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Für Familien</h3>
                <p className="text-gray-600 mb-6">Melden Sie Ihr Kind für professionelle Nachhilfe an</p>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => scrollToSection('contact')}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white transition-all duration-300 rounded-full"
                  >
                    Kostenlose Beratung
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    onClick={openWhatsApp}
                    variant="outline"
                    className="w-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50 transition-all duration-300 rounded-full"
                  >
                    <MessageCircle className="mr-2 w-5 h-5" />
                    WhatsApp Kontakt
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* For Teachers */}
            <Card className="border-2 border-teal-200 hover:border-teal-400 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 rounded-3xl overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Für Dozenten</h3>
                <p className="text-gray-600 mb-6">Werden Sie Teil unseres professionellen Teams</p>
                <Button
                  onClick={navigateToJobs}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white transition-all duration-300 rounded-full"
                >
                  Jetzt bewerben
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Courses Section with Wave Background */}
      <section id="courses" className="py-20 md:py-28 bg-gradient-to-b from-white via-teal-50/40 to-white relative">
        {/* Decorative Wave Top */}
        <div className="absolute top-0 left-0 right-0 -translate-y-1">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 0C240 60 480 80 720 60C960 40 1200 10 1440 30V0H0Z" fill="white"/>
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Unsere Leistungen
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Individuelle Förderung für jeden Schüler – von Sprachförderung bis Abiturvorbereitung
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course) => {
              const Icon = iconMap[course.icon];
              return (
                <Card
                  key={course.id}
                  className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-gray-200 rounded-3xl bg-white overflow-hidden"
                >
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors duration-300">
                      <Icon className="w-7 h-7 text-teal-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {course.title}
                    </h3>
                    <p className="text-sm text-teal-600 font-medium mb-3">
                      {course.grades}
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      {course.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section with Wave and Images */}
      <section id="about" className="py-20 md:py-28 bg-gradient-to-br from-teal-50 via-teal-100/40 to-teal-50 relative">
        {/* Wave Top */}
        <div className="absolute top-0 left-0 right-0 -translate-y-1">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 0C240 60 480 80 720 60C960 40 1200 10 1440 30V0H0Z" fill="white"/>
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Images */}
            <div className="relative order-2 lg:order-1">
              <div className="relative">
                {/* Background blob */}
                <div className="absolute -top-8 -left-8 w-72 h-72 bg-teal-200/50 rounded-full blur-2xl"></div>
                
                {/* Main Image */}
                <div className="relative">
                  <div className="absolute inset-0 bg-teal-200/40 rounded-[3rem] rotate-3 scale-105"></div>
                  <img 
                    src="https://images.pexels.com/photos/6248432/pexels-photo-6248432.jpeg"
                    alt="Schüler beim Lernen"
                    className="relative w-full h-[400px] md:h-[450px] object-cover rounded-[3rem] shadow-xl"
                  />
                </div>
                
                {/* Testimonial Card - Floating */}
                <div className="absolute -bottom-8 -right-4 md:-right-8 bg-white p-6 rounded-2xl shadow-2xl max-w-xs border border-teal-100">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-teal-600 mb-1">95%</div>
                    <div className="text-gray-600 text-sm">Erfolgsquote</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Warum das Da Vinci Institut?
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                <strong className="text-teal-700">15 erfahrene Dozenten – Examinierte Lehrkräfte mit jahrelanger Erfahrung</strong>
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Unser Team besteht aus geschulten Dozenten, die als examinierte Lehrkräfte genau wissen, 
                wie sie Wissen effektiv und altersgerecht vermitteln. Wir kombinieren pädagogisches 
                Fachwissen mit modernen Lehrmethoden für nachhaltigen Lernerfolg.
              </p>

              <div className="space-y-3">
                {[
                  'Examinierte Lehrkräfte als Dozenten',
                  'Individuelle Förderung auf jeden Schüler abgestimmt',
                  'Moderne und bewährte Lehrmethoden',
                  'Kleine Lerngruppen für optimale Betreuung',
                  'Flexible Kurszeiten für Ihre Bedürfnisse',
                  'BuT-Förderung möglich (Bildung und Teilhabe)'
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 group"
                  >
                    <div className="flex-shrink-0 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center mt-0.5 group-hover:scale-110 transition-transform duration-300">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 30C240 10 480 40 720 60C960 80 1200 60 1440 30V80H0V30Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-28 bg-white relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Jetzt Kontakt aufnehmen
            </h2>
            <p className="text-lg text-gray-600">
              Sie haben Fragen oder möchten Ihr Kind anmelden? Wir freuen uns auf Ihre Nachricht!
            </p>
            <p className="text-md text-teal-600 font-medium mt-2">
              Informieren Sie sich auch über unsere BuT-Fördermöglichkeiten
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="border-gray-200 shadow-xl rounded-3xl overflow-hidden">
                <CardContent className="p-8">
                  <ContactForm />
                </CardContent>
              </Card>
            </div>
            
            {/* Side Image */}
            <div className="lg:col-span-2 hidden lg:block">
              <div className="relative h-full">
                <div className="absolute inset-0 bg-teal-200/40 rounded-[3rem] -rotate-3 scale-105"></div>
                <img 
                  src="https://images.pexels.com/photos/6482236/pexels-photo-6482236.jpeg"
                  alt="Nachhilfe beim Da Vinci Institut"
                  className="relative w-full h-full min-h-[500px] object-cover rounded-[3rem] shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
        aria-label="WhatsApp Kontakt"
      >
        <MessageCircle className="w-7 h-7" />
      </button>
    </div>
  );
};

export default Home;
